'use client';

import { useEffect, useState } from 'react';
import type { Universe } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';

interface VideoShowcaseProps {
  universe: Universe;
  addRevealRef: AddRevealRef;
}

type ActiveMedia =
  | { type: 'single'; video: string; title: string }
  | { type: 'dual'; videoStandard: string; videoSelfie: string; name: string };

// Vidéos de démo DreamerLife, utilisées par défaut quand un univers n'a pas les siennes.
const DEFAULT_MODE_VIDEOS: { video: string; poster?: string }[] = [
  { video: '/assets/videos/non-selfie.mp4', poster: '/assets/images/non-selfie-poster.jpg' },
  { video: '/assets/videos/selfie.mp4', poster: '/assets/images/selfie-poster.jpg' },
];

const DEFAULT_STYLE_VIDEOS: { videoStandard: string; videoSelfie: string }[] = [
  { videoStandard: '/assets/videos/DL2026_Cinema_no_logo.mp4', videoSelfie: '/assets/videos/DL2026_Cinema_selfie_no_logo.mp4' },
  { videoStandard: '/assets/videos/DL2026_Classic_no_logo.mp4', videoSelfie: '/assets/videos/DL2026_Classic_selfie_no_logo.mp4' },
  { videoStandard: '/assets/videos/DL2026_Emotion_no_logo.mp4', videoSelfie: '/assets/videos/DL2026_Emotion_selfie_no_logo.mp4' },
  { videoStandard: '/assets/videos/DL2026_Fun_no_logo.mp4', videoSelfie: '/assets/videos/DL2026_Fun_selfie_no_logo.mp4' },
];

function PlayBadge({ small = false }: { small?: boolean }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
      <div
        className={`${small ? 'w-12 h-12' : 'w-16 h-16'} rounded-full flex items-center justify-center bg-white/15 backdrop-blur-md border border-white/40`}>
        <svg width={small ? 20 : 26} height={small ? 20 : 26} viewBox="0 0 24 24" fill="#fff" style={{ marginLeft: 3 }}>
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
  );
}

export default function VideoShowcase({ universe, addRevealRef }: VideoShowcaseProps) {
  const [activeMedia, setActiveMedia] = useState<ActiveMedia | null>(null);
  const [variant, setVariant] = useState<'standard' | 'selfie'>('standard');

  useEffect(() => {
    if (!activeMedia) return;
    setVariant('standard');
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveMedia(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [activeMedia]);

  const currentVideoUrl = activeMedia
    ? activeMedia.type === 'single'
      ? activeMedia.video
      : variant === 'standard'
        ? activeMedia.videoStandard
        : activeMedia.videoSelfie
    : null;

  return (
    <>
      <section id="videos" className="bg-background py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-6xl mx-auto">
          {/* Video Modes */}
          <div className="mb-20">
            <div className="mb-12">
              <p className="flex items-center text-muted text-[11px] font-semibold uppercase tracking-widest mb-4">
                <span className="accent-line" />
                2 modes de tournage
              </p>
              <h2
                ref={addRevealRef}
                className="reveal font-display text-primary"
                style={{ fontSize: 'clamp(32px, 4.5vw, 64px)', fontFamily: "'DM Serif Display', Georgia, serif" }}>
                {universe.videoTitle}{' '}
                <em className="italic text-accent">{universe.videoTitleAccent}</em>
              </h2>
              <p className="text-muted mt-3">{universe.videoDesc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {universe.videoModes.map((mode, i) => {
                const fb = DEFAULT_MODE_VIDEOS[i];
                const video = mode.video ?? fb?.video;
                const poster = mode.poster ?? fb?.poster;
                const playable = Boolean(video);
                return (
                  <div
                    key={i}
                    ref={addRevealRef}
                    onClick={() => video && setActiveMedia({ type: 'single', video, title: mode.title })}
                    className={`reveal reveal-delay-${i + 1} group bg-white border border-primary/10 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow duration-300 ${playable ? 'cursor-pointer' : ''}`}>
                    <div className="relative aspect-video overflow-hidden">
                      {playable ?
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          poster={poster}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700">
                          <source src={video} type="video/mp4" />
                        </video> :
                        <img
                          src={mode.image}
                          alt={mode.imageAlt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      }
                      {playable && <PlayBadge />}
                    </div>
                    <div className="p-6">
                      <p className="text-muted text-[10px] font-semibold uppercase tracking-widest mb-2">{mode.tag}</p>
                      <h3 className="font-semibold text-primary text-xl mb-2" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                        {mode.title}
                      </h3>
                      <p className="text-muted text-sm leading-relaxed mb-4">{mode.desc}</p>
                      <span className="inline-block bg-primary/5 text-primary/60 text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full border border-primary/10">
                        {mode.badge}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Video Styles */}
          <div>
            <div className="mb-12">
              <p className="flex items-center text-muted text-[11px] font-semibold uppercase tracking-widest mb-4">
                <span className="accent-line" />
                4 styles de montage
              </p>
              <h2
                ref={addRevealRef}
                className="reveal font-display text-primary"
                style={{ fontSize: 'clamp(32px, 4.5vw, 64px)', fontFamily: "'DM Serif Display', Georgia, serif" }}>
                Votre ambiance,{' '}
                <em className="italic text-accent">votre signature.</em>
              </h2>
              <p className="text-muted mt-3">Chaque style donne une émotion différente à votre souvenir.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {universe.videoStyles.map((style, i) => {
                const fb = DEFAULT_STYLE_VIDEOS[i];
                const std = style.videoStandard ?? fb?.videoStandard;
                const selfie = style.videoSelfie ?? fb?.videoSelfie;
                const playable = Boolean(std && selfie);
                return (
                  <div
                    key={i}
                    ref={addRevealRef}
                    onClick={() =>
                      std && selfie &&
                      setActiveMedia({ type: 'dual', videoStandard: std, videoSelfie: selfie, name: style.title })
                    }
                    className={`reveal reveal-delay-${i + 1} group bg-white border border-primary/10 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300 ${playable ? 'cursor-pointer' : ''}`}>
                    <div className="relative aspect-square overflow-hidden">
                      {playable ?
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700">
                          <source src={std} type="video/mp4" />
                        </video> :
                        <img
                          src={style.image}
                          alt={style.imageAlt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      }
                      {playable && <PlayBadge small />}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-primary text-base mb-1" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                        {style.title}
                      </h3>
                      <p className="text-muted text-xs leading-relaxed">{style.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── LIGHTBOX ── */}
      {activeMedia && currentVideoUrl &&
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/95 backdrop-blur-xl"
          onClick={() => setActiveMedia(null)}>

          <button
            onClick={() => setActiveMedia(null)}
            aria-label="Fermer"
            className="absolute top-6 right-6 md:top-8 md:right-8 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:scale-110 transition-transform duration-200">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {activeMedia.type === 'dual' &&
            <div
              className="absolute top-6 md:top-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 p-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
              onClick={(e) => e.stopPropagation()}>
              {(['standard', 'selfie'] as const).map((v) =>
                <button
                  key={v}
                  onClick={() => setVariant(v)}
                  className={`px-4 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest transition-colors duration-200 ${
                    variant === v ? 'bg-accent text-primary' : 'text-white/70 hover:text-white'
                  }`}>
                  {v === 'standard' ? 'Standard' : 'Selfie'}
                </button>
              )}
            </div>
          }

          <div
            className="w-full max-w-6xl rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <video
              key={currentVideoUrl}
              src={currentVideoUrl}
              controls
              autoPlay
              playsInline
              className="w-full h-auto block bg-black"
              style={{ aspectRatio: '16 / 9' }} />
          </div>

          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center text-white/50 text-[11px] font-semibold uppercase tracking-widest">
            {activeMedia.type === 'dual' ?
              `Style ${activeMedia.name} · Version ${variant === 'standard' ? 'Standard' : 'Selfie'}` :
              'Échap ou clic à l’extérieur pour fermer'}
          </p>
        </div>
      }
    </>
  );
}